import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import * as styles from './OptionPlanPanel.module.scss'

import {
  usePlanApi,
  usePropertyApi,
  usePublication,
  useRoomApi,
  useRouteParams,
  useSearchQueryParams,
} from '../../../hooks'
import { formatJstDate } from '../../../utils'
import { Button, Col, Container, Row, Text } from '../../basics'
import { CalendarModal, RoomSearchModal } from '../../modals'
import { Panel } from '../Panel'

const translateNamespace = 'components.Panel'

type PanelItemProps = {
  label: React.ReactNode
  value: React.ReactNode
  /**
   * When provided, render a "変更" button that invokes this callback. Used on
   * the plan-selection screen (`editable`) to open the calendar / room-search
   * modals. Omitted for read-only rows (e.g. the fixed お部屋 row) and on
   * non-editable screens.
   */
  action?: () => void
}

const PanelItem: React.FC<PanelItemProps> = ({ label, value, action }) => {
  const { t } = useTranslation()

  return (
    <div className={styles.panelItem}>
      <Container>
        <Row>
          <Col pc={action ? 9 : 12} sp={action ? 9 : 12}>
            <Text size={12} pcAlign="left" spAlign="left">
              {label}
            </Text>
            <Text size={14} weight="bold" pcAlign="left" spAlign="left">
              {value}
            </Text>
          </Col>
          {action && (
            <Col pc={3} sp={3}>
              <div className={styles.button}>
                <Button
                  display="inlineBlock"
                  htmlType="button"
                  outline
                  size="small"
                  variant="secondary"
                  onClick={action}
                >
                  {t(`${translateNamespace}.change`)}
                </Button>
              </div>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  )
}

export type OptionPlanPanelProps = {
  /**
   * When `true` (the plan-selection screen `/:property/:room/plans`), the date /
   * room-count / guest-count rows get a "変更" button that opens the calendar /
   * room-search modals. Changing a condition stays on the same screen — it only
   * updates the URL query params (with `replace: true`), so the chosen room
   * (`:room` path segment) stays fixed and the plan list re-fetches reactively.
   * See docs/specs/2026-06-06-plan-screen-search-edit-design.md.
   *
   * Defaults to `false` (read-only) so the shared option-selection screen is
   * unaffected.
   */
  editable?: boolean
}

export const OptionPlanPanel: React.FC<OptionPlanPanelProps> = ({
  editable = false,
}) => {
  const { t, i18n } = useTranslation()
  const {
    property: propertySlug,
    room: roomSlug,
    plan: planSlug,
  } = useRouteParams()
  const { params, onUpdateSearchParams } = useSearchQueryParams()
  const {
    checkInDate,
    checkOutDate,
    numberOfAdults,
    numberOfChildren,
    numberOfInfants,
    roomCount,
  } = params

  const [calendarIsOpen, setCalendarIsOpen] = useState(false)
  const [roomIsOpen, setRoomIsOpen] = useState(false)

  const { data: property } = usePropertyApi(propertySlug ?? '')
  const { data: roomResponse } = useRoomApi({
    property: propertySlug,
    room: roomSlug,
  })
  const { data: planResponse } = usePlanApi({
    property: propertySlug,
    room: roomSlug,
    plan: planSlug,
  })

  const publication = usePublication(i18n.language, property?.publications)
  const room = usePublication(i18n.language, roomResponse?.publications)
  const plan = usePublication(i18n.language, planResponse?.publications)

  const items = [
    // お部屋(roomType) is fixed by the `:room` path segment, so it has no
    // "変更" button even when editable — only the search conditions can change.
    <PanelItem
      label={t(`${translateNamespace}.roomType`)}
      value={room?.name ?? ''}
    />,
    <PanelItem
      label={t(`${translateNamespace}.checkIn`)}
      value={formatJstDate(
        checkInDate,
        i18n.language === 'ja' ? 'yyyy/MM/dd (E)' : 'MM/dd/yyyy'
      )}
      action={editable ? () => setCalendarIsOpen(true) : undefined}
    />,
    <PanelItem
      label={t(`${translateNamespace}.checkOut`)}
      value={formatJstDate(
        checkOutDate,
        i18n.language === 'ja' ? 'yyyy/MM/dd (E)' : 'MM/dd/yyyy'
      )}
      action={editable ? () => setCalendarIsOpen(true) : undefined}
    />,
    <PanelItem
      label={t(`${translateNamespace}.roomCount`)}
      value={roomCount + t(`${translateNamespace}.roomUnit`)}
      action={editable ? () => setRoomIsOpen(true) : undefined}
    />,
    <PanelItem
      label={t(`${translateNamespace}.peopleCount`)}
      value={
        <Trans
          i18nKey={`${translateNamespace}.guests`}
          value={{
            numberOfAdults,
            numberOfChildren,
            numberOfInfants,
          }}
        >
          {{ numberOfAdults }} adults
          <br />
          {{ numberOfChildren }} children
          <br />
          {{ numberOfInfants }} infants
        </Trans>
      }
      action={editable ? () => setRoomIsOpen(true) : undefined}
    />,
  ]

  if (plan) {
    items.splice(
      1,
      0,
      <PanelItem label={t(`${translateNamespace}.plan`)} value={plan.name} />
    )
  }

  return (
    <React.Fragment>
      <Panel
        cover={{
          name: publication?.name ?? '',
          image: property?.images?.[0]?.url ?? '/img/hotel01.jpg',
          desc: plan
            ? t(`${translateNamespace}.action.option`)
            : t(`${translateNamespace}.action.plan`),
        }}
        label={t(`${translateNamespace}.label.room`)}
        items={items}
      />
      {editable && (
        <React.Fragment>
          <CalendarModal
            propertyName={publication?.name ?? ''}
            checkInParam={checkInDate}
            checkOutParam={checkOutDate}
            isOpen={calendarIsOpen}
            onClose={() => setCalendarIsOpen(false)}
            onSearch={(checkIn, checkOut) => {
              // Stay on the plan screen: only update the URL query params (with
              // replace so changing conditions doesn't pollute history). The
              // `:room` path segment is untouched, so the chosen room stays
              // fixed and PlanIndex re-fetches reactively via useSearchQueryParams.
              onUpdateSearchParams(
                { checkInDate: checkIn, checkOutDate: checkOut },
                true
              )
              setCalendarIsOpen(false)
            }}
            key={`${checkInDate}${checkOutDate}`}
          />
          <RoomSearchModal
            isOpen={roomIsOpen}
            onClose={() => setRoomIsOpen(false)}
            propertyName={publication?.shortName ?? ''}
            officialUrl={property?.officialUrl ?? ''}
            propertySlug={property?.slug}
            roomParam={roomCount}
            adultParam={numberOfAdults}
            childParam={numberOfChildren}
            infantParam={numberOfInfants}
            checkIn={checkInDate}
            checkOut={checkOutDate}
            onSearch={(rooms, adults, children, infant) => {
              onUpdateSearchParams(
                {
                  roomCount: rooms,
                  numberOfAdults: adults,
                  numberOfChildren: children,
                  numberOfInfants: infant,
                },
                true
              )
              setRoomIsOpen(false)
            }}
            key={`${roomCount}${numberOfAdults}${numberOfChildren}${numberOfInfants}`}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  )
}
